import React from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { bankList, formatAccountNumber } from '../../Utils/bankList';
import { formatBusinessNumber } from '../../Utils/businessNumber';

export default function MainAccountLinkoPayCard(props) {
  const {
    data,
    businessRegistrationNumber,
    representativeName,
    email,
    bankName,
    accountNumber,
    settleCycle,
    settleCycleUnit,
    onClickApply,
    onClickModify
  } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const isJoined = data !== undefined;
  const normalizedBankName = bankName ? bankName.replace(/\s+/g, '') : '';
  const matchedBank = bankName ? (bankList.find(bank => bank.name === normalizedBankName) || null) : null;
  const bankLogo = matchedBank?.logo || null;
  const formattedAccountNumber = formatAccountNumber(matchedBank, accountNumber);

  // 정산 주기 일수 계산 //
  function calcSettlementDays() {
    if (settleCycleUnit === 'month') return settleCycle * 30;
    else if (settleCycleUnit === 'week') return settleCycle * 7;
    else return settleCycle;
  }

  // 가입 상태일 때만 값, 미가입은 미입력 placeholder //
  function valueOrEmpty(value) {
    if (!isJoined) return t('myStoreSetting.linkoPay.card.empty');
    if (value === undefined || value === null || value === '') return t('myStoreSetting.linkoPay.card.empty');

    return value;
  }

  // sellerStatus → 모달 mode 매핑 //
  function getModalMode() {
    if (!isJoined) return 'apply';

    const status = data.sellerStatus;

    if (status === 'PARTIALLY_APPROVED' || status === 'APPROVED') return 'active';
    else if (status === 'REJECTED') return 'rejected';
    else return 'reviewing';
  }

  // 수정하기 버튼 클릭 - 현재 status에 맞는 mode 전달 //
  function onClickModifyButton() {
    if (onClickModify) onClickModify(getModalMode());
  }

  // 상태 pill 분기 (신청 심사중 / 반려) //
  function renderStatusPills() {
    if (!isJoined) return null;

    const status = data.sellerStatus;
    const isReviewing = status === 'APPROVAL_REQUIRED' || status === 'KYC_REQUIRED' || status === undefined;
    const isActive = status === 'PARTIALLY_APPROVED' || status === 'APPROVED';
    const isRejected = status === 'REJECTED';

    return (
      <PillGroup>
        {
          isActive &&
          <CyclePill borderColor={theme.primary} backgroundColor={theme.primaryAlpha20}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('myStoreSetting.linkoPay.card.cycleFormat').replace('{days}', calcSettlementDays())}
            </Text>
          </CyclePill>
        }
        {
          isReviewing &&
          <CyclePill borderColor={theme.primary} backgroundColor={theme.primaryAlpha20}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('myStoreSetting.linkoPay.card.statusReviewing')}
            </Text>
          </CyclePill>
        }
        {
          isRejected &&
          <RejectPill borderColor={theme.pointRed} backgroundColor={theme.pointRedAlpha20}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('myStoreSetting.linkoPay.card.statusRejected')}
            </Text>
          </RejectPill>
        }
      </PillGroup>
    );
  }

  return (
    <Card backgroundColor={theme.subBackground}>
      <FieldGrid>
        <LeftColumn>
          <FieldItem>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>
              {t('myStoreSetting.linkoPay.card.ownerName')}
            </Text>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {valueOrEmpty(representativeName)}
            </Text>
          </FieldItem>

          <FieldItem>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>
              {t('myStoreSetting.linkoPay.card.email')}
            </Text>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {valueOrEmpty(email)}
            </Text>
          </FieldItem>
        </LeftColumn>

        <VerticalDivider backgroundColor={theme.line} />

        <RightColumn>
          <FieldItem>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>
              {t('myStoreSetting.linkoPay.card.businessNumber')}
            </Text>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {valueOrEmpty(businessRegistrationNumber && formatBusinessNumber(businessRegistrationNumber))}
            </Text>
          </FieldItem>

          <FieldItem>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>
              {t('myStoreSetting.linkoPay.card.settlementBankAccount')}
            </Text>
            {
              isJoined ?
                <BankAccountRow>
                  {
                    bankLogo ?
                      <BankLogoImage src={bankLogo} alt={bankName} />
                      :
                      <BankLogoPlaceholder backgroundColor={theme.line} />
                  }
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{bankName || ''}</Text>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{formattedAccountNumber}</Text>
                </BankAccountRow>
                :
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.card.empty')}
                </Text>
            }
          </FieldItem>
        </RightColumn>
      </FieldGrid>

      <HorizontalDivider backgroundColor={theme.line} />

      <ActionRow>
        <CycleInfoGroup>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>
            {t('myStoreSetting.linkoPay.card.settlementCycle')}
          </Text>
          {
            !isJoined &&
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
              {t('myStoreSetting.linkoPay.card.settlementInactive')}
            </Text>
          }
          {renderStatusPills()}
        </CycleInfoGroup>
        {
          isJoined ?
            <ModifyButton backgroundColor={theme.buttonFill3} onClick={onClickModifyButton}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                {t('myStoreSetting.linkoPay.card.modifyButton')}
              </Text>
            </ModifyButton>
            :
            <ApplyButton backgroundColor={theme.primary} onClick={onClickApply}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.background}>
                {t('myStoreSetting.linkoPay.card.applyButton')}
              </Text>
            </ApplyButton>
        }
      </ActionRow>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  flex: 1 0 0;
  min-width: 0;

  padding: 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const FieldGrid = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: stretch;

  gap: 20px;

  width: 100%;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 20px;

  flex: 0 0 auto;
  width: fit-content;
  min-width: 180px;
  max-width: 220px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 20px;

  flex: 1 0 0;
  min-width: 0;
`;

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: stretch;

  gap: 10px;

  height: 56px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const BankAccountRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
  height: 30px;
`;

const BankLogoPlaceholder = styled.div`
  width: 32px;
  height: 23px;

  border-radius: 3px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const BankLogoImage = styled.img`
  width: 32px;
  height: 23px;

  object-fit: contain;
  border-radius: 3px;

  flex-shrink: 0;
`;

const HorizontalDivider = styled.div`
  width: 100%;
  height: 1px;

  border-radius: 300px;
  background-color: ${props => props.backgroundColor};

  margin: 10px 0;
`;

const VerticalDivider = styled.div`
  width: 1px;

  border-radius: 300px;
  background-color: ${props => props.backgroundColor};

  align-self: stretch;

  flex-shrink: 0;
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const CycleInfoGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const PillGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;
`;

const CyclePill = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 5px 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const RejectPill = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 5px 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const ModifyButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  min-width: 72px;
  padding: 5px 10px;
  box-sizing: border-box;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ApplyButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  min-width: 72px;
  padding: 5px 10px;
  box-sizing: border-box;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

